<template>
  <Modal ref="modal" class="send-approval-modal client-digest-modal">
    <template v-if="!created">
      <h2 class="box__title">
        {{ $t('clientDigest.title', { name: scopeName }) }}
      </h2>
      <p class="send-approval__intro">{{ $t('clientDigest.intro') }}</p>
      <div v-if="nps && nps.responses" class="client-digest__nps">
        <i class="iconoir-star"></i>
        {{ $t('clientDigest.nps') }}
        <strong>{{ nps.nps > 0 ? '+' : '' }}{{ nps.nps }}</strong>
        · {{ $t('clientDigest.npsResponses', { count: nps.responses }) }}
      </div>

      <FormGroup :label="$t('clientDigest.kind')" required>
        <div class="client-digest__kinds">
          <a
            v-for="kind in kinds"
            :key="kind.key"
            class="client-digest__kind"
            :class="{ 'client-digest__kind--active': periodKind === kind.key }"
            @click="setKind(kind.key)"
          >
            <i :class="kind.icon"></i> {{ kind.label }}
          </a>
        </div>
      </FormGroup>

      <FormGroup :label="$t('clientDigest.period')" required>
        <div class="client-digest__period">
          <input v-model="periodStart" type="date" class="input" @input="periodKind = 'custom'" />
          <span>{{ $t('clientDigest.periodTo') }}</span>
          <input v-model="periodEnd" type="date" class="input" @input="periodKind = 'custom'" />
          <a class="client-digest__quick" @click="setLastWeek()">
            {{ $t('clientDigest.lastWeek') }}
          </a>
          <a class="client-digest__quick" @click="setLastMonth()">
            {{ $t('clientDigest.lastMonth') }}
          </a>
        </div>
      </FormGroup>

      <!-- Prévia -->
      <div class="client-digest__preview">
        <div v-if="previewLoading" class="client-digest__preview-loading">
          <div class="loading"></div>
        </div>
        <template v-else-if="preview">
          <div class="client-digest__totals">
            <span class="client-digest__total client-digest__total--done">
              <strong>{{ preview.totals.done }}</strong>
              {{ $t('digestPage.tiles.done') }}
            </span>
            <span class="client-digest__total client-digest__total--progress">
              <strong>{{ preview.totals.progress }}</strong>
              {{ $t('digestPage.tiles.progress') }}
            </span>
            <span class="client-digest__total client-digest__total--next">
              <strong>{{ preview.totals.next }}</strong>
              {{ $t('digestPage.groups.next') }}
            </span>
            <span class="client-digest__total client-digest__total--waiting">
              <strong>{{ preview.totals.waiting }}</strong>
              {{ $t('digestPage.tiles.waiting') }}
            </span>
          </div>
          <div
            v-if="!preview.tables.length && !preview.waiting.length"
            class="client-digest__empty"
          >
            {{ $t('clientDigest.emptyPreview') }}
          </div>
          <div v-else class="client-digest__tables">
            <div
              v-for="section in preview.tables"
              :key="section.id"
              class="client-digest__table"
            >
              <strong>{{ section.name }}</strong>
              <span v-if="section.done.length">
                ✓ {{ section.done.map((r) => r.title).join(', ') }}
              </span>
              <span v-if="section.progress.length">
                ↻ {{ section.progress.map((r) => r.title).join(', ') }}
              </span>
              <span v-if="section.next.length">
                → {{ section.next.map((r) => r.title).join(', ') }}
              </span>
            </div>
          </div>
        </template>
      </div>

      <FormGroup :label="$t('clientDigest.note')">
        <textarea
          v-model="note"
          class="input send-approval__textarea"
          rows="3"
          :placeholder="$t('clientDigest.notePlaceholder')"
          maxlength="10000"
        ></textarea>
      </FormGroup>

      <FormGroup :label="$t('sendApproval.contact')">
        <div v-if="contacts.length" class="send-approval__recent">
          <a
            v-for="(contact, index) in contacts"
            :key="index"
            class="send-approval__recent-chip"
            @click="applyContact(contact)"
          >
            {{ contact.name || contact.phone || contact.email }}
          </a>
        </div>
        <div class="send-approval__contact-grid">
          <input
            v-model="contactName"
            class="input"
            :placeholder="$t('sendApproval.contactName')"
            maxlength="255"
          />
          <input
            v-model="contactPhone"
            class="input"
            :placeholder="$t('sendApproval.contactPhone')"
            maxlength="64"
          />
          <input
            v-model="contactEmail"
            class="input"
            :placeholder="$t('sendApproval.contactEmail')"
            maxlength="255"
          />
        </div>
      </FormGroup>

      <div class="actions actions--right">
        <Button
          type="primary"
          size="large"
          :loading="saving"
          :disabled="saving || previewLoading || !periodStart || !periodEnd"
          @click="submit"
        >
          {{ $t('clientDigest.create') }}
        </Button>
      </div>

      <!-- Anteriores -->
      <div v-if="digests.length" class="client-digest__history">
        <div class="row-approvals__section">
          {{ $t('clientDigest.previous') }}
        </div>
        <div
          v-for="digest in digests"
          :key="digest.id"
          class="client-digest__history-item"
          :class="{
            'client-digest__history-item--revoked': digest.status === 'revoked',
          }"
        >
          <span class="client-digest__history-title">{{ digest.title }}</span>
          <span class="client-digest__history-meta">
            {{ formatDate(digest.created_on) }} ·
            {{ $t('clientDigest.views', { count: digest.view_count }) }}
          </span>
          <a
            class="row-approvals__action"
            :title="$t('rowApprovals.copyLink')"
            @click="copy(digest.public_url)"
          >
            <i class="iconoir-copy"></i>
          </a>
          <a
            class="row-approvals__action row-approvals__action--danger"
            :title="
              digest.status === 'revoked'
                ? $t('clientDigest.reactivate')
                : $t('rowApprovals.revoke')
            "
            @click="toggleRevoke(digest)"
          >
            <i
              :class="
                digest.status === 'revoked' ? 'iconoir-refresh' : 'iconoir-lock'
              "
            ></i>
          </a>
        </div>
      </div>
    </template>

    <template v-else>
      <h2 class="box__title">{{ $t('sendApproval.readyTitle') }}</h2>
      <p class="send-approval__ready-text">
        {{ $t('clientDigest.readyText') }}
      </p>
      <div class="send-approval__link">
        <input
          class="input"
          :value="created.public_url"
          readonly
          @focus="$event.target.select()"
        />
        <Button type="secondary" icon="iconoir-copy" @click="copy(created.public_url)">
          {{ $t('sendApproval.copy') }}
        </Button>
      </div>
      <div class="send-approval__channels">
        <Button type="primary" icon="iconoir-chat-bubble" @click="openWhatsApp">
          WhatsApp
        </Button>
        <Button
          type="secondary"
          icon="iconoir-send-mail"
          :loading="sendingEmail"
          :disabled="!contactEmail || sendingEmail"
          @click="sendEmail"
        >
          {{ $t('sendApproval.sendEmail') }}
        </Button>
        <Button type="secondary" @click="hide()">
          {{ $t('sendApproval.done') }}
        </Button>
      </div>
      <input
        v-model="contactEmail"
        class="input client-digest__email"
        :placeholder="$t('sendApproval.contactEmail')"
        maxlength="255"
      />
    </template>
  </Modal>
</template>

<script>
import modal from '@baserow/modules/core/mixins/modal'
import { notifyIf } from '@baserow/modules/core/utils/error'
import ApprovalsService from '@baserow/modules/core/services/approvals'
import { copyToClipboard } from '@baserow/modules/database/utils/clipboard'
import { buildDigestShareMessage } from '@baserow/modules/database/utils/approvalShare'

function isoDay(date) {
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${date.getFullYear()}-${m}-${d}`
}

export default {
  name: 'ClientDigestModal',
  mixins: [modal],
  props: {
    database: { type: Object, required: true },
    // Relatório por tabela (= cliente); sem ela, o espaço inteiro.
    table: { type: Object, required: false, default: null },
  },
  data() {
    return {
      periodStart: '',
      periodEnd: '',
      periodKind: 'week',
      note: '',
      contactName: '',
      contactPhone: '',
      contactEmail: '',
      contacts: [],
      digests: [],
      nps: null,
      preview: null,
      previewLoading: false,
      previewTimer: null,
      saving: false,
      sendingEmail: false,
      created: null,
    }
  },
  computed: {
    service() {
      return ApprovalsService(this.$client)
    },
    scopeName() {
      return this.table ? this.table.name : this.database.name
    },
    kinds() {
      return [
        { key: 'week', label: this.$t('clientDigest.weekly'), icon: 'iconoir-calendar' },
        { key: 'month', label: this.$t('clientDigest.monthly'), icon: 'iconoir-calendar-plus' },
        { key: 'custom', label: this.$t('clientDigest.custom'), icon: 'iconoir-edit-pencil' },
      ]
    },
  },
  watch: {
    periodStart() {
      this.schedulePreview()
    },
    periodEnd() {
      this.schedulePreview()
    },
  },
  methods: {
    show(...args) {
      this.reset()
      this.load()
      return modal.methods.show.call(this, ...args)
    },
    reset() {
      this.periodKind = 'week'
      this.setKind('week')
      this.note = ''
      this.contactName = ''
      this.contactPhone = ''
      this.contactEmail = ''
      this.preview = null
      this.created = null
      this.saving = false
    },
    setKind(kind) {
      this.periodKind = kind
      if (kind === 'custom') return
      const today = new Date()
      const start = new Date(today)
      start.setDate(today.getDate() - (kind === 'month' ? 29 : 6))
      this.periodStart = isoDay(start)
      this.periodEnd = isoDay(today)
    },
    setLastMonth() {
      const today = new Date()
      const first = new Date(today.getFullYear(), today.getMonth() - 1, 1)
      const last = new Date(today.getFullYear(), today.getMonth(), 0)
      this.periodKind = 'month'
      this.periodStart = isoDay(first)
      this.periodEnd = isoDay(last)
    },
    setLastWeek() {
      const today = new Date()
      const dow = (today.getDay() + 6) % 7 // segunda = 0
      const monday = new Date(today)
      monday.setDate(today.getDate() - dow - 7)
      const sunday = new Date(monday)
      sunday.setDate(monday.getDate() + 6)
      this.periodKind = 'week'
      this.periodStart = isoDay(monday)
      this.periodEnd = isoDay(sunday)
    },
    async load() {
      try {
        const { data } = this.table
          ? await this.service.fetchTableDigests(this.table.id)
          : await this.service.fetchDigests(this.database.id)
        this.digests = data.digests
        this.contacts = data.contacts
        const { data: nps } = await this.service.fetchNps(this.database.id)
        this.nps = nps
      } catch (e) {
        this.digests = []
        this.contacts = []
      }
      this.schedulePreview()
    },
    schedulePreview() {
      clearTimeout(this.previewTimer)
      if (!this.periodStart || !this.periodEnd) return
      this.previewTimer = setTimeout(() => this.fetchPreview(), 350)
    },
    async fetchPreview() {
      this.previewLoading = true
      try {
        const values = {
          period_start: this.periodStart,
          period_end: this.periodEnd,
          period_kind: this.periodKind,
        }
        const { data } = this.table
          ? await this.service.previewTableDigest(this.table.id, values)
          : await this.service.previewDigest(this.database.id, values)
        this.preview = data.content
      } catch (e) {
        this.preview = null
      }
      this.previewLoading = false
    },
    applyContact(contact) {
      this.contactName = contact.name || this.contactName
      this.contactPhone = contact.phone || this.contactPhone
      this.contactEmail = contact.email || this.contactEmail
    },
    async submit() {
      this.saving = true
      try {
        const values = {
          period_start: this.periodStart,
          period_end: this.periodEnd,
          period_kind: this.periodKind,
          note: this.note,
          contact_name: this.contactName,
          contact_phone: this.contactPhone,
          contact_email: this.contactEmail,
        }
        const { data } = this.table
          ? await this.service.createTableDigest(this.table.id, values)
          : await this.service.createDigest(this.database.id, values)
        this.created = data.digest
        this.digests.unshift(data.digest)
      } catch (error) {
        notifyIf(error, 'clientDigest')
      }
      this.saving = false
    },
    copy(url) {
      copyToClipboard(url)
      this.$store.dispatch('toast/info', {
        title: this.$t('rowApprovals.copiedTitle'),
        message: this.$t('rowApprovals.copied'),
      })
    },
    openWhatsApp() {
      const message = buildDigestShareMessage(this.created)
      const digits = (this.contactPhone || '').replace(/\D/g, '')
      const url = digits
        ? `https://wa.me/${digits}?text=${encodeURIComponent(message)}`
        : `https://wa.me/?text=${encodeURIComponent(message)}`
      window.open(url, '_blank', 'noopener')
    },
    async sendEmail() {
      this.sendingEmail = true
      try {
        await this.service.sendDigestEmail(this.created.id, this.contactEmail)
        this.$store.dispatch('toast/success', {
          title: this.$t('sendApproval.emailSentTitle'),
          message: this.$t('sendApproval.emailSent', { to: this.contactEmail }),
        })
      } catch (error) {
        notifyIf(error, 'clientDigest')
      }
      this.sendingEmail = false
    },
    async toggleRevoke(digest) {
      try {
        const { data } = await this.service.updateDigest(digest.id, {
          revoked: digest.status !== 'revoked',
        })
        Object.assign(digest, data.digest)
      } catch (error) {
        notifyIf(error, 'clientDigest')
      }
    },
    formatDate(iso) {
      return new Date(iso).toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
      })
    },
  },
}
</script>
